import { showDateOfBirthAgeRestrictionDialog } from 'src/brm/actions/age-restriction/components/date-of-birth-age-restriction-modal';
import { showEmeraldSelectionDialog } from 'src/common/components/interactive-dialogue/components/selection-dialogue/selection-dialog';
import { SelectListItem } from 'src/common/components/select-list/select-list';
import { IReasonCode } from 'src/common/interfaces/dtos/IReasonCode';
import { showApproveRejectAgeRestrictionDialog } from '../../../../brm/actions/age-restriction/components/approve-reject-age-restriction-modal';
import { showDOBApproveRejectAgeRestrictionDialog } from '../../../../brm/actions/age-restriction/components/dob-approve-reject-age-restriction-modal';
import { popupMessage } from '../../../../messages/business-logic/popup-message';
import { popEmeraldModal } from '../../../emerald-modal-manager/emerald-modal-manager';
import { getFormattedDate, isDateAfterToday, isDateValid } from '../../../helpers/date-helper';
import { ICustomerAgeAction } from '../brm-utils';

export enum AgeRestrictionApprovalType {
  Verify = 'Verify',
  Approved = 'Approved',
  Rejected = 'Rejected',
  Scanned = 'Scanned',
}
export interface BrmInputs {
  approvalType?: AgeRestrictionApprovalType;
  subAspectType?: string;
  isApproved?: boolean;
  customerBirthDate?: string;
  rejectReasonCode?: string;
  scanData?: string;
  action?: ICustomerAgeAction;
}

export const handleAgeRestrictionBRM = (
  customerAgeAction: ICustomerAgeAction,
  onAccept: (brmInputs?: BrmInputs) => void,
  onCancel: () => void,
): void => {
  const { reasons, approveRejectRequired, birthDateRequired, expectedAge } = customerAgeAction;

  const onApprove = (): void => {
    onAccept({
      approvalType: AgeRestrictionApprovalType.Approved,
      isApproved: true,
      action: customerAgeAction,
    });
  };

  const onAcceptReject = (): void => {
    onAccept({ approvalType: AgeRestrictionApprovalType.Rejected, isApproved: false });
  };

  const onBarcodeScanned = async (scanData: string): Promise<void> => {
    onAccept({ approvalType: AgeRestrictionApprovalType.Scanned, scanData: scanData });
  };

  const onDateVerify = (dateString?: string): void => {
    const date = validateDate(dateString);
    if (date) {
      onAccept({
        approvalType: AgeRestrictionApprovalType.Verify,
        isApproved: true,
        customerBirthDate: date,
      });
    }
  };

  const onAcceptRejectReasonCode = (reasonCode: string): void => {
    onAccept({
      approvalType: AgeRestrictionApprovalType.Rejected,
      isApproved: false,
      rejectReasonCode: reasonCode,
    });
  };

  const onValidateReasonCode = (): void => {
    if (reasons?.length) {
      if (reasons.length === 1 && reasons[0].isDefault) {
        onAcceptRejectReasonCode(reasons[0].value);
      } else {
        popEmeraldModal();
        const selectListItems = reasons.map(
          (r: IReasonCode): SelectListItem => ({
            id: r.value,
            description: r.description,
          }),
        );
        const modalProps = {
          selectListItems,
          title: 'AgeRestriction_Title',
          message: 'AgeRestriction_RejectBecause',
          acceptLabel: 'AgeRestrictionContent_Ok',
          onCancel,
          onAccept: onAcceptRejectReasonCode,
          dataTestId: 'reason-code-required',
        };
        showEmeraldSelectionDialog(modalProps);
      }
    } else {
      onAcceptReject();
    }
  };

  if (birthDateRequired && !approveRejectRequired) {
    const modalProps = {
      expectedAge,
      errorMessage: customerAgeAction.message?.message,
      onVerify: onDateVerify,
      onClose: onCancel,
    };
    showDateOfBirthAgeRestrictionDialog(modalProps, onBarcodeScanned);
  } else if (birthDateRequired) {
    const modalProps = {
      expectedAge,
      errorMessage: customerAgeAction.message?.message,
      onApprove,
      onVerify: onDateVerify,
      onReject: onValidateReasonCode,
      onClose: onCancel,
    };
    showDOBApproveRejectAgeRestrictionDialog(modalProps, onBarcodeScanned);
  } else {
    const modalProps = {
      onClose: onCancel,
      onApprove,
      onReject: onValidateReasonCode,
      brmCaption: customerAgeAction.message?.caption,
      brmMessage: customerAgeAction.message?.message,
    };
    showApproveRejectAgeRestrictionDialog(modalProps);
  }
};

const validateDate = (dateString?: string): string | undefined => {
  if (dateString && dateString.length === 10) {
    if (!isDateValid(dateString)) {
      popupMessage({ message: 'WrongDateFormat' });
    } else if (isDateAfterToday(dateString)) {
      popupMessage({ message: `UnderAgedCustomer` });
    } else {
      const formattedDate = getFormattedDate(dateString);
      if (formattedDate) {
        return formattedDate;
      }
    }
  } else {
    popupMessage({ message: 'WrongDateFormat' });
  }
};
